import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { Box } from '../Box'
import { Price } from '../Price'
import { Text, Title } from '../Text'
import { Contract } from '../../redux/account/user'
import { getCountryCode, getPricesMode, getShowAvailableBalance } from '../../redux/config'
import { useProductDomain } from '../../hooks/useProductDomain'
import { useUmbracoProducts } from '../../hooks/useUmbracoProducts'
import { ContractType } from '../../redux/user'
import { Spin, SpinSize } from '../../components/Spin'
import { Flex } from '../Flex'
import { useAccountingStatus } from '../../hooks/useAccountingStatus'

const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minMax(100px, 225px));
  grid-gap: 0rem;
  grid-auto-flow: row;
`
export type ContactProps = {
  primaryContract: Contract
}

export const PrepaidContractInfo = ({ primaryContract }: ContactProps) => {
  const { t } = useTranslation(['account', 'core', 'checkout'])
  const pricesMode = useSelector(getPricesMode)
  const domainCode = useSelector(getCountryCode)
  const showAvailableBalance = useSelector(getShowAvailableBalance)
  const ta = (key: string) => t(`account:Accounting.${key}`)

  const reportCodes = _.compact(_.map(primaryContract?.reportPackages, rp => rp.reportCode))
  const { productsDomainData, loading: productDomainLoading } = useProductDomain(
    {
      domainCode: domainCode,
      accountingCodes: reportCodes
    },
    reportCodes.length === 0
  )
  const umbracoProductCodes = productsDomainData ? _.compact(_.map(productsDomainData, pd => pd.productCode)) : []
  const { data: umbracoProducts, loading: umbracoProductLoading } = useUmbracoProducts(
    umbracoProductCodes,
    umbracoProductCodes.length === 0
  )

  const isSapOnline = useAccountingStatus()

  const showAvailableReport = primaryContract.reportPackages && primaryContract.reportPackages.length > 0
  const showAvailBalanceReportBlock =
    !productDomainLoading && !umbracoProductLoading && (showAvailableReport || showAvailableBalance)

  return (
    <>
      {(productDomainLoading || umbracoProductLoading) && <Spin delay={0} spinning size={SpinSize.Default} />}
      {showAvailBalanceReportBlock && (
        <Flex flexDirection="row" flexWrap="wrap" pb={2}>
          <Box width={[1, 3 / 5, 3 / 4]} pr={[0, 2, 2]} pb={[1, 0, 0]}>
            {showAvailableReport ? (
              <>
                <Title level="small" bold>
                  {ta('AvailableReports')}
                </Title>

                {_.map(primaryContract.reportPackages, rp => {
                  const domainProduct = _.find(productsDomainData, y => y.accountingCode === rp.reportCode)
                  const umbracoData = _.find(umbracoProducts, y => y.bifCode === domainProduct?.productCode)
                  return (
                    <Box pt={2}>
                      <Container>
                        <Text pt={1}>{umbracoData?.name}</Text>
                        <Text pt={1}>{`${rp.consumedReports}/${rp.totalReports}`}</Text>
                      </Container>
                    </Box>
                  )
                })}
              </>
            ) : showAvailableBalance ? (
              <>
                <Title level="small" bold>
                  {ta('AvailableBalance')}
                </Title>
                <Box pt={2}>
                  <Text pt={1}>
                    {Price({
                      pricesMode,
                      contractType: ContractType.Prepaid,
                      grossValue: primaryContract?.displayPrepaidBalanceGross || '',
                      netValue: primaryContract?.displayPrepaidBalanceNet || ''
                    })}
                  </Text>
                </Box>
              </>
            ) : null}
          </Box>
        </Flex>
      )}
      {!isSapOnline.loading && isSapOnline.isOffline && isSapOnline.disclaimers?.visibility?.showInBalance && (
        <Text pt={1} pb={1} pr={1} color="danger">
          {'*' + t('checkout:ServiceUnavailable')}
        </Text>
      )}
    </>
  )
}
